import { supabase } from '@/integrations/supabase/client';
import { measurementAllowed } from '@/lib/editorialAttribution';
export const EMAIL_KEY = 'loftloom_email_attribution';
export const EMAIL_TTL = 30 * 86400000;
export interface EmailSource { token: string; at: number; campaign?: string; campaignAt?: number }
export function campaignFromSearch(search: string): string | undefined {
  const p = new URLSearchParams(search);
  const campaign = p.get('utm_campaign') || '';
  return p.get('utm_medium')?.toLowerCase() === 'email' && /^[a-z0-9][a-z0-9_-]{0,79}$/.test(campaign) ? campaign : undefined;
}
export function validEmailSource(value: unknown, now = Date.now()): value is EmailSource {
  if (!value || typeof value !== 'object') return false;
  const s = value as EmailSource;
  return typeof s.token === 'string' && /^[0-9a-f-]{36}$/.test(s.token) && Number.isFinite(s.at) && s.at <= now && now - s.at < EMAIL_TTL;
}
export function getEmailSource(): EmailSource | undefined {
  try {
    if (!measurementAllowed()) { localStorage.removeItem(EMAIL_KEY); return; }
    const s: unknown = JSON.parse(localStorage.getItem(EMAIL_KEY) || 'null');
    if (!validEmailSource(s)) { localStorage.removeItem(EMAIL_KEY); return; }
    return { token: s.token, at: s.at, campaign: s.campaignAt && s.campaignAt <= Date.now() && Date.now() - s.campaignAt < 30 * 60000 ? s.campaign : undefined };
  } catch { return; }
}
let pending: Promise<void> | undefined;
let signupPending: Promise<void> | undefined;
let lastCampaign: string | undefined;
export function rememberEmailSignup(email: string, source: 'homepage' | 'footer' | 'exit_popup' | 'cart_save' | 'style_quiz'): Promise<void> {
  if (signupPending) return signupPending;
  signupPending = saveSignup(email, source).finally(() => { signupPending = undefined; });
  return signupPending;
}
async function saveSignup(email: string, source: 'homepage' | 'footer' | 'exit_popup' | 'cart_save' | 'style_quiz') {
  if (!measurementAllowed()) return;
  if (pending) await pending;
  const previous = getEmailSource();
  const token = crypto.randomUUID();
  const campaign = campaignFromSearch(window.location.search) || previous?.campaign;
  const { data, error } = await supabase.functions.invoke('email-attribution', { body: { action: 'signup', token, email, source, campaign } });
  if (!error && data?.at && measurementAllowed()) localStorage.setItem(EMAIL_KEY, JSON.stringify({ token, at: Date.parse(data.at), campaign, campaignAt: campaign ? Date.now() : undefined }));
}
export function captureEmailCampaign(search: string) {
  const campaign = campaignFromSearch(search);
  if (!campaign || !measurementAllowed() || pending || lastCampaign === campaign) return;
  pending = (async () => {
    let s = getEmailSource();
    if (!s) {
      const token = crypto.randomUUID();
      const { data, error } = await supabase.functions.invoke('email-attribution', { body: { action: 'return', token, campaign } });
      if (error || !data?.at || !measurementAllowed()) return;
      s = { token, at: Date.parse(data.at) };
    }
    if (!measurementAllowed()) return;
    localStorage.setItem(EMAIL_KEY, JSON.stringify({ ...s, campaign, campaignAt: Date.now() }));
    lastCampaign = campaign;
    await saveEmailOutcome('email_visit', undefined, 1);
  })().catch(() => undefined).finally(() => { pending = undefined; });
}
export async function saveEmailOutcome(event: 'product_view' | 'cart_add' | 'email_visit', product?: string, quantity = 1) {
  if (signupPending && event !== 'email_visit') await signupPending;
  if (pending && event !== 'email_visit') await pending;
  const s = getEmailSource();
  if (!s) return;
  await supabase.functions.invoke('email-attribution', { body: { action: 'event', token: s.token, id: crypto.randomUUID(), event, product, quantity: Math.min(quantity, 100), campaign: s.campaign } }).catch(() => undefined);
}
export async function checkoutEmailSource() { if (signupPending) await signupPending; if (pending) await pending; return getEmailSource(); }